import { useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  useToast,
  Heading,
  Text,
  useColorModeValue,
  InputGroup,
  InputRightElement,
  IconButton
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import { useNavigate, Link } from 'react-router-dom';
import { userService } from '../config/index';
import { UserRole } from '../types/user';
import { useAuth } from '../components/AuthProvider';

export const AdminUserForm = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<UserRole>('Arquitecto');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();
  const brandRed = useColorModeValue('red.500', 'red.300');
  const { currentUser } = useAuth(); // Usar el hook de autenticación para obtener el usuario actual

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (!currentUser) {
        throw new Error('No hay usuario autenticado');
      }

      // Usar el servicio de usuario desde configuración centralizada (Convex)
      await userService.createUser(email, password, role, currentUser.email || 'system');
      
      toast({
        title: 'Usuario creado',
        description: `Se ha creado el usuario ${email} con rol ${role}`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      
      navigate('/admin/users');
    } catch (error) {
      console.error('Error al crear usuario:', error);
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido al crear usuario';
      toast({
        title: 'Error al crear usuario',
        description: errorMessage,
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box 
      minH="100vh"
      bgGradient={useColorModeValue(
        'linear(to-br, blue.100, pink.100)',
        'linear(to-br, blue.900, red.900)'
      )}
      py={6}
    >
      <Box maxW="500px" mx="auto" px={6}>
        <VStack spacing={8} align="stretch">
          <Box 
            position="relative"
            bg="whiteAlpha.500"
            p={{ base: 2, md: 3 }}
            borderRadius="xl"
            backdropFilter="blur(10px)"
            display="flex"
            alignItems="center"
            minH={{ base: "60px", md: "70px" }}
          >
            <Link to="/">
              <Box 
                as="img" 
                src="/famma.png" 
                alt="Logo Famma" 
                className="logo" 
                h={{ base: "45px", md: "55px" }}
                objectFit="contain"
                transition="transform 0.2s ease"
                _hover={{ transform: 'scale(1.05)' }}
                ml={{ base: 2, md: 3 }}
              />
            </Link>
            <Box flex="1" textAlign="center">
              <Heading
                as="h1"
                fontSize={{ base: '2xl', md: '3xl', lg: '4xl' }}
                fontWeight="bold"
                color="brand.600"
                letterSpacing="tight"
                lineHeight="shorter"
              >
                Crear Nuevo Usuario
              </Heading>
            </Box>
            <Box w={{ base: "45px", md: "55px" }} /> {/* Spacer para balancear el logo */}
          </Box>

          <Box textAlign="center">
            <Heading as="h1" size="xl" mb={2} color={brandRed}>
              Crear Nuevo Usuario
            </Heading>
            <Text color="gray.600">
              Complete el formulario para crear un nuevo usuario
            </Text>
          </Box>

          <Box 
            as="form" 
            onSubmit={handleSubmit}
            bg="white" 
            p={6} 
            borderRadius="lg" 
            boxShadow="md"
            border="1px"
            borderColor="gray.200"
          >
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Email</FormLabel>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Contraseña</FormLabel>
                <InputGroup>
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <InputRightElement>
                    <IconButton
                      aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                      icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                      variant="ghost"
                      onClick={() => setShowPassword(!showPassword)}
                      size="sm"
                      color="gray.500"
                      _hover={{
                        color: 'red.500',
                      }}
                    />
                  </InputRightElement>
                </InputGroup>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Rol</FormLabel>
                <Select
                  value={role}
                  onChange={(e) => setRole(e.target.value as UserRole)}
                >
                  <option value="Admin">Administrador</option>
                  <option value="Arquitecto">Arquitecto</option>
                  <option value="Jurídico">Jurídico</option>
                  <option value="Administrativo">Administrativo</option>
                </Select>
              </FormControl>

              <Button
                type="submit"
                colorScheme="red"
                size="lg"
                width="full"
                mt={4}
                isLoading={isLoading}
              >
                Crear Usuario
              </Button>
            </VStack>
          </Box>
        </VStack>
      </Box>
    </Box>
  );
};
